/**
 * CertificateModal — Create / Edit certificate modal
 *
 * Schema fields (from schema-compliance.ts / compliance-memberships route):
 *  - certificateType: diploma | cert_iv | degree | accreditation | lender_accreditation | other
 *  - holderType: organisation | credit_representative | staff
 *  - certificateStatus: current | expired | revoked | pending_renewal
 *  - issuingBody (NOT issuingAuthority)
 *  - holderId + holderName (for CR linking)
 *  - No annualComplianceDate in schema — field omitted
 *  - issueDate is REQUIRED (notNull in schema)
 */

import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CertificateData {
  id?: string;
  certificateType?: string;
  certificateName?: string;
  certificateNumber?: string | null;
  holderType?: string;
  holderId?: string | null;
  holderName?: string | null;
  issuingBody?: string;
  issueDate?: string;
  expiryDate?: string | null;
  certificateStatus?: string;
  certificateUrl?: string | null;
  notes?: string | null;
}

interface CertificateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingCertificate?: CertificateData | null;
  onSuccess?: () => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CERTIFICATE_TYPES = [
  { value: "diploma", label: "Diploma" },
  { value: "cert_iv", label: "Certificate IV" },
  { value: "degree", label: "Degree" },
  { value: "accreditation", label: "Accreditation" },
  { value: "lender_accreditation", label: "Lender Accreditation" },
  { value: "other", label: "Other" },
];

const HOLDER_TYPES = [
  { value: "organisation", label: "Organisation" },
  { value: "credit_representative", label: "Credit Representative" },
  { value: "staff", label: "Staff" },
];

const CERTIFICATE_STATUSES = [
  { value: "current", label: "Current" },
  { value: "pending_renewal", label: "Pending Renewal" },
  { value: "expired", label: "Expired" },
  { value: "revoked", label: "Revoked" },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CertificateModal({
  open,
  onOpenChange,
  editingCertificate,
  onSuccess,
}: CertificateModalProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const isEditMode = !!editingCertificate?.id;

  // Form state
  const [certificateType, setCertificateType] = useState("");
  const [certificateName, setCertificateName] = useState("");
  const [certificateNumber, setCertificateNumber] = useState("");
  const [holderType, setHolderType] = useState("organisation");
  const [holderId, setHolderId] = useState("");
  const [holderName, setHolderName] = useState("");
  const [issuingBody, setIssuingBody] = useState("");
  const [issueDate, setIssueDate] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [certificateStatus, setCertificateStatus] = useState("current");
  const [certificateUrl, setCertificateUrl] = useState("");
  const [notes, setNotes] = useState("");

  // Reset / pre-populate form when modal opens
  useEffect(() => {
    if (open && editingCertificate) {
      setCertificateType(editingCertificate.certificateType ?? "");
      setCertificateName(editingCertificate.certificateName ?? "");
      setCertificateNumber(editingCertificate.certificateNumber ?? "");
      setHolderType(editingCertificate.holderType ?? "organisation");
      setHolderId(editingCertificate.holderId ?? "");
      setHolderName(editingCertificate.holderName ?? "");
      setIssuingBody(editingCertificate.issuingBody ?? "");
      setIssueDate(editingCertificate.issueDate ?? "");
      setExpiryDate(editingCertificate.expiryDate ?? "");
      setCertificateStatus(editingCertificate.certificateStatus ?? "current");
      setCertificateUrl(editingCertificate.certificateUrl ?? "");
      setNotes(editingCertificate.notes ?? "");
    } else if (open && !editingCertificate) {
      setCertificateType("");
      setCertificateName("");
      setCertificateNumber("");
      setHolderType("organisation");
      setHolderId("");
      setHolderName("");
      setIssuingBody("");
      setIssueDate("");
      setExpiryDate("");
      setCertificateStatus("current");
      setCertificateUrl("");
      setNotes("");
    }
  }, [open, editingCertificate]);

  const isValid =
    certificateType.length > 0 &&
    certificateName.trim().length > 0 &&
    issuingBody.trim().length > 0 &&
    issueDate.length > 0;

  const handleOpenChange = (newOpen: boolean) => {
    onOpenChange(newOpen);
  };

  // ---------------------------------------------------------------------------
  // Mutations
  // ---------------------------------------------------------------------------

  const createMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest(
        "POST",
        "/api/compliance/certificates",
        {
          certificateType,
          certificateName: certificateName.trim(),
          certificateNumber: certificateNumber.trim() || undefined,
          holderType,
          holderId: holderId.trim() || undefined,
          holderName: holderName.trim() || undefined,
          issuingBody: issuingBody.trim(),
          issueDate,
          expiryDate: expiryDate || undefined,
          certificateStatus,
          certificateUrl: certificateUrl.trim() || undefined,
          notes: notes.trim() || undefined,
        }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(
          (err as { error?: string }).error ?? "Failed to create certificate"
        );
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/compliance/certificates"],
      });
      toast({ title: "Certificate created successfully" });
      handleOpenChange(false);
      onSuccess?.();
    },
    onError: (err: Error) => {
      toast({
        title: "Error",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const editMutation = useMutation({
    mutationFn: async () => {
      if (!editingCertificate?.id) throw new Error("No certificate ID");
      const response = await apiRequest(
        "PATCH",
        `/api/compliance/certificates/${editingCertificate.id}`,
        {
          certificateType,
          certificateName: certificateName.trim(),
          certificateNumber: certificateNumber.trim() || undefined,
          holderType,
          holderId: holderId.trim() || undefined,
          holderName: holderName.trim() || undefined,
          issuingBody: issuingBody.trim(),
          issueDate,
          expiryDate: expiryDate || undefined,
          certificateStatus,
          certificateUrl: certificateUrl.trim() || undefined,
          notes: notes.trim() || undefined,
        }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(
          (err as { error?: string }).error ?? "Failed to update certificate"
        );
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/compliance/certificates"],
      });
      toast({ title: "Certificate updated successfully" });
      handleOpenChange(false);
      onSuccess?.();
    },
    onError: (err: Error) => {
      toast({
        title: "Error",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const isPending = createMutation.isPending || editMutation.isPending;

  const handleSubmit = () => {
    if (!isValid) return;
    if (isEditMode) {
      editMutation.mutate();
    } else {
      createMutation.mutate();
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="modal-certificate-form"
      >
        <DialogHeader>
          <DialogTitle>
            {isEditMode ? "Edit Certificate" : "Add Certificate"}
          </DialogTitle>
          <DialogDescription>
            {isEditMode
              ? "Update the certificate details below."
              : "Enter the certificate details below."}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          {/* Row 1: Type + Status */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="certificate-type">
                Type <span className="text-destructive">*</span>
              </Label>
              <Select
                value={certificateType}
                onValueChange={setCertificateType}
              >
                <SelectTrigger
                  id="certificate-type"
                  data-testid="select-certificate-type"
                >
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {CERTIFICATE_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="certificate-status">Status</Label>
              <Select
                value={certificateStatus}
                onValueChange={setCertificateStatus}
              >
                <SelectTrigger
                  id="certificate-status"
                  data-testid="select-certificate-status"
                >
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {CERTIFICATE_STATUSES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Certificate Name */}
          <div className="space-y-1.5">
            <Label htmlFor="certificate-name">
              Certificate Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="certificate-name"
              data-testid="input-certificate-name"
              value={certificateName}
              onChange={(e) => setCertificateName(e.target.value)}
              placeholder="e.g. Diploma of Finance and Mortgage Broking"
            />
          </div>

          {/* Certificate Number */}
          <div className="space-y-1.5">
            <Label htmlFor="certificate-number">Certificate Number</Label>
            <Input
              id="certificate-number"
              data-testid="input-certificate-number"
              value={certificateNumber}
              onChange={(e) => setCertificateNumber(e.target.value)}
              placeholder="e.g. CERT-12345"
            />
          </div>

          {/* Holder Type */}
          <div className="space-y-1.5">
            <Label htmlFor="holder-type">
              Holder Type <span className="text-destructive">*</span>
            </Label>
            <Select value={holderType} onValueChange={setHolderType}>
              <SelectTrigger
                id="holder-type"
                data-testid="select-holder-type"
              >
                <SelectValue placeholder="Select holder type" />
              </SelectTrigger>
              <SelectContent>
                {HOLDER_TYPES.map((h) => (
                  <SelectItem key={h.value} value={h.value}>
                    {h.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Holder Name + Holder ID (for CR linking) */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="holder-name">Holder Name</Label>
              <Input
                id="holder-name"
                data-testid="input-holder-name"
                value={holderName}
                onChange={(e) => setHolderName(e.target.value)}
                placeholder="Name of certificate holder"
              />
            </div>
            {holderType === "credit_representative" && (
              <div className="space-y-1.5">
                <Label htmlFor="holder-id">
                  Credit Representative ID
                </Label>
                <Input
                  id="holder-id"
                  data-testid="input-holder-id"
                  value={holderId}
                  onChange={(e) => setHolderId(e.target.value)}
                  placeholder="UUID of the credit representative"
                />
              </div>
            )}
          </div>

          {/* Issuing Body */}
          <div className="space-y-1.5">
            <Label htmlFor="issuing-body">
              Issuing Body <span className="text-destructive">*</span>
            </Label>
            <Input
              id="issuing-body"
              data-testid="input-issuing-body"
              value={issuingBody}
              onChange={(e) => setIssuingBody(e.target.value)}
              placeholder="e.g. ASIC, MFAA, AAMC"
            />
          </div>

          {/* Dates row */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="issue-date">
                Issue Date <span className="text-destructive">*</span>
              </Label>
              <Input
                id="issue-date"
                data-testid="input-issue-date"
                type="date"
                value={issueDate}
                onChange={(e) => setIssueDate(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="expiry-date-cert">Expiry Date</Label>
              <Input
                id="expiry-date-cert"
                data-testid="input-expiry-date"
                type="date"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
              />
            </div>
          </div>

          {/* Certificate URL */}
          <div className="space-y-1.5">
            <Label htmlFor="certificate-url">Certificate URL</Label>
            <Input
              id="certificate-url"
              data-testid="input-certificate-url"
              value={certificateUrl}
              onChange={(e) => setCertificateUrl(e.target.value)}
              placeholder="https://sharepoint.com/..."
            />
          </div>

          {/* Notes */}
          <div className="space-y-1.5">
            <Label htmlFor="cert-notes">Notes</Label>
            <Textarea
              id="cert-notes"
              data-testid="textarea-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Additional notes..."
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!isValid || isPending}
            data-testid="button-submit-certificate"
          >
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isEditMode ? "Save Changes" : "Add Certificate"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
